import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  Renderer2,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Utils } from '@app/shared/helpers/utils';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocumentProxy, version } from 'pdfjs-dist';
import { Dialog } from 'primeng/dialog';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss']
})
export class PdfViewerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('pdfViewer') pdfViewer: Dialog;
  @ViewChild('pdfContainer', { static: true }) pdfContainerRef!: ElementRef;
  @ViewChild('sidebar', { static: true }) sidebarRef!: ElementRef;

  pdfDoc: PDFDocumentProxy | null = null;
  pageInput = 1;
  currentPage = 1;
  totalPages = 0;
  isManuallyEditingPageInput = false;

  zoom = 1.0;
  zoomPercent = 100;

  loadingError = false;
  allPagesCanvases: HTMLCanvasElement[] = [];
  showSidebar = true;
  thumbnails: HTMLCanvasElement[] = [];
  isMobile = false;
  isLoading = false;

  constructor(
    public matDialogRef: MatDialogRef<PdfViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { file: File },
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
    this.isMobile = window.innerWidth <= 768;
  }

  ngAfterViewInit(): void {
    this.loadPDF(this.data.file);
    this.pdfViewer.maximize();
  }

  ngOnDestroy(): void {
    // Clean up any object URLs if needed
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') this.goToNextPage();
    if (event.key === 'ArrowLeft') this.goToPreviousPage();
  }

  async loadPDF(file: File) {
    try {
      this.isLoading = true;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      this.pdfDoc = pdf;
      this.totalPages = pdf.numPages;
      setTimeout(() => this.renderAllThumbnails(), 0);
      this.allPagesCanvases = [];
      await this.renderAllPages();
      this.isLoading = false;
    } catch (err) {
      this.loadingError = true;
      this.isLoading = false;
      console.error('Erro ao carregar PDF:', err);
    }
  }

  async renderAllPages() {
    const container = this.pdfContainerRef.nativeElement;
    container.innerHTML = '';
    this.allPagesCanvases = [];

    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      const page = await this.pdfDoc!.getPage(pageNum);
      const viewport = page.getViewport({ scale: this.zoom });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.dataset.pageNumber = String(pageNum);

      await page.render({ canvasContext: context, viewport }).promise;

      canvas.style.display = 'block';
      canvas.style.margin = '1rem auto';

      this.renderer.appendChild(container, canvas);
      this.allPagesCanvases.push(canvas);
    }

    this.observeScroll();
  }

  async renderAllThumbnails() {
    const sidebar = this.sidebarRef.nativeElement;
    sidebar.innerHTML = '';
    this.thumbnails = [];

    for (let i = 1; i <= this.totalPages; i++) {
      const page = await this.pdfDoc!.getPage(i);
      const viewport = page.getViewport({ scale: 0.25 });
      const canvas = this.renderer.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      canvas.classList.add('thumbnail');
      this.renderer.listen(canvas, 'click', () => this.navigateToPage(i));

      this.thumbnails.push(canvas);
      this.renderer.appendChild(sidebar, canvas);
    }
    this.highlightThumbnail(this.currentPage);
    this.cdr.detectChanges();
  }

  highlightThumbnail(pageNum: number) {
    this.thumbnails.forEach((thumb, index) => {
      if (index + 1 === pageNum) {
        thumb.classList.add('active');
        thumb.scrollIntoView({
          behavior: 'smooth',
          inline: this.isMobile ? 'center' : 'nearest',
          block: this.isMobile ? 'nearest' : 'center'
        });
      } else {
        thumb.classList.remove('active');
      }
    });
  }

  toggleSidebar() {
    this.showSidebar = !this.showSidebar;
  }

  navigateToPage(pageNum: number) {
    if (pageNum < 1) pageNum = 1;
    if (pageNum > this.totalPages) pageNum = this.totalPages;

    this.currentPage = pageNum;
    this.pageInput = pageNum;

    setTimeout(() => {
      const canvas = this.allPagesCanvases[pageNum - 1];
      if (canvas) {
        canvas.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.highlightThumbnail(pageNum);
      } else {
        console.warn(`Canvas da página ${pageNum} ainda não foi renderizado.`);
      }
    }, 50);
  }

  observeScroll() {
    const container = this.pdfContainerRef.nativeElement;

    container.addEventListener('scroll', () => {
      // if (this.isManuallyEditingPageInput) return;

      let closestPage = 1;
      let minOffset = Infinity;

      this.allPagesCanvases.forEach((canvas, index) => {
        const rect = canvas.getBoundingClientRect();
        const offset = Math.abs(rect.top - 100);
        if (offset < minOffset) {
          minOffset = offset;
          closestPage = index + 1;
        }
      });

      if (this.currentPage !== closestPage) {
        this.currentPage = closestPage;
        this.pageInput = closestPage;
        this.highlightThumbnail(this.currentPage);
      }
    });
  }

  async zoomIn() {
    this.zoom += 0.1;
    this.zoomPercent = Math.round(this.zoom * 100);
    await this.renderAllPages();

    this.scrollToCurrentCanvas();
  }

  async zoomOut() {
    if (this.zoom > 0.3) {
      this.zoom = -0.1;
      this.zoomPercent = Math.round(this.zoom * 100);
      await this.renderAllPages();

      this.scrollToCurrentCanvas();
    }
  }

  async onZoomInputChange() {
    let newZoom = this.zoomPercent;

    if (newZoom < 25) {
      newZoom = 25;
    } else if (newZoom > 500) {
      newZoom = 500;
    }

    this.zoomPercent = newZoom;
    this.zoom = Utils.converteABNT(newZoom / 100, 2);

    await this.renderAllPages();
    this.scrollToCurrentCanvas();
  }

  scrollToCurrentCanvas() {
    const targetCanvas = this.allPagesCanvases[this.currentPage - 1];
    setTimeout(() => {
      targetCanvas?.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 50);
  }

  preventInvalidCharacters(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];

    const isNumberKey = /^\d$/.test(event.key);
    const isAllowed = isNumberKey || allowedKeys.includes(event.key);

    if (!isAllowed) {
      event.preventDefault();
    }
  }

  preventPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
    }
  }

  onPageInputChange(event: Event) {
    // this.isManuallyEditingPageInput = false;

    const inputValue = (event.target as HTMLInputElement).value;
    let pageNum = parseInt(inputValue, 10);

    if (isNaN(pageNum)) {
      this.pageInput = this.currentPage;
      return;
    }

    pageNum = Math.max(1, Math.min(this.totalPages, pageNum));
    this.navigateToPage(pageNum);
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pageInput = this.currentPage;
      this.navigateToPage(this.currentPage);
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageInput = this.currentPage;
      this.navigateToPage(this.currentPage);
    }
  }

  printPdf() {
    const fileURL = URL.createObjectURL(this.data.file);
    const iframe = document.createElement('iframe');

    iframe.style.position = 'fixed';
    iframe.style.visibility = 'hidden';
    iframe.src = fileURL;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // Cleanup após imprimir
      // setTimeout(() => {
      //   document.body.removeChild(iframe);
      //   URL.revokeObjectURL(fileURL);
      // }, 1000);
    };
  }

  downloadPdf() {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(this.data.file);
    link.download = this.data.file.name || 'arquivo.pdf';
    link.click();
  }
}
